// frontend/src/pages/configuraciones/ConfiguracionClientesAlquiler.jsx
// Clientes a los que se les alquilan equipos, con sus personas de contacto.
//
// Los contactos se editan dentro del mismo modal del cliente: son varios por
// cliente y no tienen sentido por separado. Al crear un contrato de alquiler se
// propone el contacto marcado como principal.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useToast } from '../../components/common';
import rentalService from '../../services/rentalService';
import {
  Page, Header, Title, Controls, SearchInput, BackBtn, AddBtn,
  TableWrap, Table, Th, Td, ActionsCell, ActionBtn,
  Overlay, ModalBox, ModalHead, ModalTitle, CloseBtn, ModalBody, ModalFoot,
  FormGrp, Lbl, Inp, Row2, ErrMsg, BtnPrimary, BtnSecondary, BtnDanger,
} from '../../components/configuracion/logisticaLayoutStyles';
import HelpPanel, { HelpToggleBtn } from '../../components/common/HelpPanel';
import ImportarModal from '../../components/importacion/ImportarModal';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';

const Textarea = styled.textarea`
  width: 100%; background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.95rem;
  min-height: 70px; box-sizing: border-box; resize: vertical;
  font-family: inherit;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
`;

const Select = styled.select`
  width: 100%; background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme }) => theme.colors.txtPrincipal};
  padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.95rem;
  box-sizing: border-box; cursor: pointer;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
  option { background: ${({ theme }) => theme.colors.surface}; }
`;

const SubTitulo = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin: 1rem 0 0.5rem; padding-top: 0.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  font-size: 0.8rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.04em; color: ${({ theme }) => theme.colors.txtSecondary};
`;

const TablaContactos = styled.table`
  width: 100%; border-collapse: collapse; font-size: 0.85rem;
  th {
    text-align: left; padding: 0.3rem 0.4rem; font-size: 0.7rem;
    text-transform: uppercase; letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.txtSecondary};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  }
  td {
    padding: 0.25rem 0.4rem; vertical-align: middle;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  }
  input[type="text"], input[type="email"] {
    width: 100%; box-sizing: border-box;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.borderDefault};
    color: ${({ theme }) => theme.colors.txtPrincipal};
    padding: 0.3rem 0.4rem; border-radius: 4px; font-size: 0.85rem;
    &:focus { outline: none; border-color: ${({ theme }) => theme.colors.info}; }
  }
`;

const MiniBtn = styled.button`
  background: none; border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  color: ${({ theme, $danger }) => $danger ? theme.colors.danger : theme.colors.txtSecondary};
  border-radius: 4px; padding: 0.15rem 0.45rem; font-size: 0.75rem; cursor: pointer;
  &:hover { border-color: ${({ theme, $danger }) => $danger ? theme.colors.danger : theme.colors.primary}; }
`;

const Chip = styled.span`
  display: inline-block; padding: 0.05rem 0.4rem; border-radius: 4px;
  font-size: 0.7rem; font-weight: 600;
  background: ${({ theme }) => theme.colors.bgSecundario};
  color: ${({ theme }) => theme.colors.txtSecondary};
`;

const EMPTY = {
  razon_social: '', cuit: '', condicion_iva: 'responsable_inscripto',
  direccion: '', localidad: '', provincia: '',
  telefono: '', email: '', observaciones: '', activo: true,
};

const CONDICIONES_IVA = [
  { valor: 'responsable_inscripto', etiqueta: 'Responsable Inscripto' },
  { valor: 'monotributo', etiqueta: 'Monotributo' },
  { valor: 'exento', etiqueta: 'Exento' },
  { valor: 'consumidor_final', etiqueta: 'Consumidor Final' },
];

const CONTACTO_VACIO = { nombre: '', cargo: '', telefono: '', email: '', principal: false };

export default function ConfiguracionClientesAlquiler({
  modalOnly = false, onRecordSaved = null, onRequestClose = null,
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modal, setModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [contactos, setContactos] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const { onBackdropMouseDown, buildOnBackdropClick } = useBackdropCloseGuard();

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await rentalService.listarClientes({ page_size: 500 });
      setItems(r.results || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (modalOnly) { abrir(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtrados = useMemo(() => {
    if (!busqueda.trim()) return items;
    const q = busqueda.toLowerCase();
    return items.filter(i =>
      i.razon_social?.toLowerCase().includes(q) ||
      i.cuit?.toLowerCase().includes(q) ||
      i.localidad?.toLowerCase().includes(q) ||
      i.email?.toLowerCase().includes(q)
    );
  }, [items, busqueda]);

  const set = (campo, valor) => {
    setForm(f => ({ ...f, [campo]: valor }));
    setErrors(e => (e[campo] ? { ...e, [campo]: undefined } : e));
  };

  const abrir = (item = null) => {
    setEditando(item);
    setForm(item ? {
      razon_social: item.razon_social || '',
      cuit: item.cuit || '',
      condicion_iva: item.condicion_iva || 'responsable_inscripto',
      direccion: item.direccion || '',
      localidad: item.localidad || '',
      provincia: item.provincia || '',
      telefono: item.telefono || '',
      email: item.email || '',
      observaciones: item.observaciones || '',
      activo: item.activo !== false,
    } : EMPTY);
    setContactos(item?.contactos ? item.contactos.map(c => ({ ...c })) : []);
    setErrors({});
    setModal(true);
  };

  const cerrar = () => {
    setModal(false);
    setEditando(null);
    setContactos([]);
    if (modalOnly) onRequestClose?.();
  };

  // ── Contactos ─────────────────────────────────────────────────────────────
  const agregarContacto = () => setContactos(cs => [...cs, { ...CONTACTO_VACIO }]);

  const setContacto = (indice, campo, valor) => {
    setContactos(cs => cs.map((c, i) => (i === indice ? { ...c, [campo]: valor } : c)));
  };

  // Solo un contacto puede ser el principal: marcar uno desmarca al anterior.
  const marcarPrincipal = (indice) => {
    setContactos(cs => cs.map((c, i) => ({ ...c, principal: i === indice })));
  };

  const quitarContacto = (indice) => {
    setContactos(cs => cs.filter((_, i) => i !== indice));
  };

  const guardar = async () => {
    if (!form.razon_social.trim()) {
      setErrors({ razon_social: 'La razón social es obligatoria.' });
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      const payload = {
        ...form,
        contactos: contactos
          .filter(c => c.nombre?.trim())
          .map(c => ({
            nombre: c.nombre, cargo: c.cargo || '',
            telefono: c.telefono || '', email: c.email || '',
            principal: !!c.principal,
          })),
      };
      const guardado = editando
        ? await rentalService.actualizarCliente(editando.id, payload)
        : await rentalService.crearCliente(payload);

      if (modalOnly && onRecordSaved) {
        onRecordSaved(guardado);
        onRequestClose?.();
      } else {
        cerrar();
        cargar();
      }
    } catch (err) {
      const detail = err.response?.data;
      if (detail && typeof detail === 'object') setErrors(detail);
    } finally { setSaving(false); }
  };

  const eliminar = async (id) => {
    try {
      await rentalService.eliminarCliente(id);
      setConfirmId(null);
      cargar();
    } catch (err) {
      setConfirmId(null);
      const detalle = err.response?.data?.detail;
      // El backend protege con PROTECT: un cliente con contratos no se borra.
      toast.error(detalle || 'No se pudo eliminar el cliente. Puede tener alquileres registrados.');
    }
  };

  const cuerpoModal = (
    <>
      <FormGrp>
        <Lbl>Razón social *</Lbl>
        <Inp autoFocus value={form.razon_social}
          onChange={e => set('razon_social', e.target.value)}
          placeholder="Nombre de la empresa que alquila" />
        {errors.razon_social && <ErrMsg>{String(errors.razon_social)}</ErrMsg>}
      </FormGrp>

      <Row2>
        <FormGrp>
          <Lbl>CUIT</Lbl>
          <Inp value={form.cuit} onChange={e => set('cuit', e.target.value)}
            placeholder="30-12345678-9" />
          {errors.cuit && <ErrMsg>{String(errors.cuit)}</ErrMsg>}
        </FormGrp>
        <FormGrp>
          <Lbl>Condición frente al IVA</Lbl>
          <Select value={form.condicion_iva} onChange={e => set('condicion_iva', e.target.value)}>
            {CONDICIONES_IVA.map(c => (
              <option key={c.valor} value={c.valor}>{c.etiqueta}</option>
            ))}
          </Select>
        </FormGrp>
      </Row2>

      <FormGrp>
        <Lbl>Dirección</Lbl>
        <Inp value={form.direccion} onChange={e => set('direccion', e.target.value)}
          placeholder="Calle y número" />
      </FormGrp>

      <Row2>
        <FormGrp>
          <Lbl>Localidad</Lbl>
          <Inp value={form.localidad} onChange={e => set('localidad', e.target.value)} />
        </FormGrp>
        <FormGrp>
          <Lbl>Provincia</Lbl>
          <Inp value={form.provincia} onChange={e => set('provincia', e.target.value)} />
        </FormGrp>
      </Row2>

      <Row2>
        <FormGrp>
          <Lbl>Teléfono</Lbl>
          <Inp value={form.telefono} onChange={e => set('telefono', e.target.value)} />
        </FormGrp>
        <FormGrp>
          <Lbl>Correo electrónico</Lbl>
          <Inp type="email" value={form.email} onChange={e => set('email', e.target.value)}
            placeholder="correo@ejemplo.com" />
        </FormGrp>
      </Row2>

      <SubTitulo>
        <span>Contactos</span>
        <MiniBtn type="button" onClick={agregarContacto}>
          <i className="fas fa-plus" /> Agregar contacto
        </MiniBtn>
      </SubTitulo>

      {contactos.length === 0 ? (
        <div style={{ fontSize: '0.85rem', opacity: 0.7, padding: '0.5rem 0' }}>
          Sin contactos cargados. El contacto principal es el que se propone al crear un alquiler.
        </div>
      ) : (
        <TablaContactos>
          <thead>
            <tr>
              <th style={{ width: '28%' }}>Nombre</th>
              <th style={{ width: '20%' }}>Cargo</th>
              <th style={{ width: '20%' }}>Teléfono</th>
              <th style={{ width: '24%' }}>Email</th>
              <th style={{ width: '8%' }}>Princ.</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {contactos.map((c, i) => (
              <tr key={c.id || `nuevo-${i}`}>
                <td>
                  <input type="text" value={c.nombre || ''}
                    onChange={e => setContacto(i, 'nombre', e.target.value)}
                    placeholder="Nombre y apellido" />
                </td>
                <td>
                  <input type="text" value={c.cargo || ''}
                    onChange={e => setContacto(i, 'cargo', e.target.value)} />
                </td>
                <td>
                  <input type="text" value={c.telefono || ''}
                    onChange={e => setContacto(i, 'telefono', e.target.value)} />
                </td>
                <td>
                  <input type="email" value={c.email || ''}
                    onChange={e => setContacto(i, 'email', e.target.value)} />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input type="radio" name="contacto-principal"
                    checked={!!c.principal}
                    onChange={() => marcarPrincipal(i)} />
                </td>
                <td style={{ textAlign: 'right' }}>
                  <MiniBtn type="button" $danger onClick={() => quitarContacto(i)}>×</MiniBtn>
                </td>
              </tr>
            ))}
          </tbody>
        </TablaContactos>
      )}

      <FormGrp style={{ marginTop: '0.75rem' }}>
        <Lbl>Observaciones</Lbl>
        <Textarea value={form.observaciones}
          onChange={e => set('observaciones', e.target.value)} />
      </FormGrp>

      <FormGrp>
        <Lbl style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.activo}
            onChange={e => set('activo', e.target.checked)} />
          Cliente activo
        </Lbl>
      </FormGrp>
    </>
  );

  if (modalOnly) {
    return modal ? (
      <Overlay onClick={buildOnBackdropClick(cerrar)} onMouseDown={onBackdropMouseDown}>
        <ModalBox $maxWidth="680px" onClick={e => e.stopPropagation()}>
          <ModalHead>
            <ModalTitle>Nuevo cliente de alquiler</ModalTitle>
            <CloseBtn type="button" tabIndex={-1} onClick={cerrar}>×</CloseBtn>
          </ModalHead>
          <ModalBody>{cuerpoModal}</ModalBody>
          <ModalFoot>
            <BtnSecondary onClick={cerrar}>Cancelar</BtnSecondary>
            <BtnPrimary onClick={guardar} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar'}
            </BtnPrimary>
          </ModalFoot>
        </ModalBox>
      </Overlay>
    ) : null;
  }

  return (
    <Page>
      <Header>
        <Title><i className="fas fa-handshake" /> Clientes de alquiler</Title>
        <HelpToggleBtn type="button" $active={helpOpen} onClick={() => setHelpOpen(v => !v)}>
          <i className={helpOpen ? 'bi bi-question-circle-fill' : 'bi bi-question-circle'} />
          Ayuda
        </HelpToggleBtn>
      </Header>

      {helpOpen && (
        <div style={{ marginBottom: '1rem' }}>
          <HelpPanel
            tips={[
              'Cargá acá las empresas a las que les alquilás equipos.',
              'Cada cliente puede tener varios contactos. El marcado como principal es el que se propone al crear un alquiler nuevo.',
              'Un cliente que ya tiene alquileres registrados no se puede eliminar: desmarcá "Cliente activo" para que deje de aparecer al crear contratos.',
            ]}
            nextSteps={[
              'Crear un alquiler desde el módulo Rental eligiendo este cliente.',
              'Consultar el informe de facturación de alquileres para ver cuánto se le factura a cada uno.',
            ]}
            inlineOpen
          />
        </div>
      )}

      <Controls>
        <SearchInput placeholder="Buscar cliente..." value={busqueda}
          onChange={e => setBusqueda(e.target.value)} />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }}>
          <BackBtn onClick={() => navigate('/rental')}>
            <i className="fas fa-arrow-left" /> Volver
          </BackBtn>
          <BackBtn onClick={() => setShowImportModal(true)}>
            <i className="fas fa-file-import" /> Importar
          </BackBtn>
          <AddBtn onClick={() => abrir()}><i className="fas fa-plus" /> Agregar</AddBtn>
        </div>
      </Controls>

      <TableWrap>
        <Table>
          <thead>
            <tr>
              <Th>Razón social</Th>
              <Th>CUIT</Th>
              <Th>Localidad</Th>
              <Th>Contacto principal</Th>
              <Th>Teléfono</Th>
              <Th>Alquileres activos</Th>
              <Th>Equipos</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><Td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>Cargando...</Td></tr>
            )}
            {!loading && filtrados.length === 0 && (
              <tr><Td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>Sin registros</Td></tr>
            )}
            {filtrados.map(item => {
              const principal = (item.contactos || []).find(c => c.principal)
                || (item.contactos || [])[0];
              return (
                <tr key={item.id} style={{ opacity: item.activo === false ? 0.55 : 1 }}>
                  <Td>
                    {item.razon_social}
                    {item.activo === false && <> <Chip>Inactivo</Chip></>}
                  </Td>
                  <Td>{item.cuit || '-'}</Td>
                  <Td>{item.localidad || '-'}</Td>
                  <Td>{principal?.nombre || '-'}</Td>
                  <Td>{principal?.telefono || item.telefono || '-'}</Td>
                  <Td>{item.contratos_activos ?? 0}</Td>
                  <Td>{item.equipos_alquilados ?? 0}</Td>
                  <ActionsCell>
                    <ActionBtn onClick={() => abrir(item)}>Editar</ActionBtn>
                    <ActionBtn $danger onClick={() => setConfirmId(item.id)}>Eliminar</ActionBtn>
                  </ActionsCell>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableWrap>

      {modal && (
        <Overlay onClick={buildOnBackdropClick(cerrar)} onMouseDown={onBackdropMouseDown}>
          <ModalBox $maxWidth="680px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>{editando ? 'Editar cliente' : 'Nuevo cliente de alquiler'}</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={cerrar}>×</CloseBtn>
            </ModalHead>
            <ModalBody>{cuerpoModal}</ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={cerrar}>Cancelar</BtnSecondary>
              <BtnPrimary onClick={guardar} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </BtnPrimary>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {confirmId && (
        <Overlay onClick={buildOnBackdropClick(() => setConfirmId(null))} onMouseDown={onBackdropMouseDown}>
          <ModalBox $maxWidth="420px" onClick={e => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>Eliminar cliente</ModalTitle>
              <CloseBtn type="button" tabIndex={-1} onClick={() => setConfirmId(null)}>×</CloseBtn>
            </ModalHead>
            <ModalBody>
              ¿Eliminar este cliente? Si ya tiene alquileres registrados no se va a poder
              borrar: en ese caso, desmarcá "Cliente activo" para que deje de aparecer.
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={() => setConfirmId(null)}>Cancelar</BtnSecondary>
              <BtnDanger onClick={() => eliminar(confirmId)}>Eliminar</BtnDanger>
            </ModalFoot>
          </ModalBox>
        </Overlay>
      )}

      {showImportModal && (
        <ImportarModal
          entidad="clientes-alquiler"
          onClose={() => setShowImportModal(false)}
          onImportado={cargar}
        />
      )}
    </Page>
  );
}
